import { JSX } from "react";
import { DOMConversionMap, DOMConversionOutput, LexicalNode, NodeKey, SerializedLexicalNode, Spread } from "lexical";
import { $applyNodeReplacement, DecoratorNode, DOMExportOutput } from "lexical";

// Import EquationComponent
import EquationComponent from "./EquationComponent.tsx";

export type SerializedEquationNode = Spread<
  {
    equation: string;
    inline: boolean;
  },
  SerializedLexicalNode
>;

function $convertEquationElement(domNode: HTMLElement): null | DOMConversionOutput {
  let equation = domNode.getAttribute("data-lexical-equation");
  const inline = domNode.getAttribute("data-lexical-inline") === "true";

  // Try to decode equation from base64 attribute
  if (equation) {
    try {
      equation = atob(equation);
    } catch (e) {
      // If decoding fails, use the attribute value as-is
      console.warn("Failed to decode equation from base64:", e);
    }
  }

  // Fallback: try to extract equation from KaTeX-rendered HTML
  if (!equation || equation.trim() === "") {
    // Look for a data attribute that might contain the raw equation
    const dataEquationAttr = Object.keys(domNode.attributes).find((key) =>
      domNode.attributes[key as unknown as number].name.includes("equation")
    );

    if (dataEquationAttr) {
      equation = domNode.getAttribute(dataEquationAttr);
    }
  }

  if (equation) {
    const node = $createEquationNode(equation, inline);
    return { node };
  }

  return null;
}

export class EquationNode extends DecoratorNode<JSX.Element> {
  __equation: string;
  __inline: boolean;

  static getType(): string {
    return "equation";
  }

  static clone(node: EquationNode): EquationNode {
    return new EquationNode(node.__equation, node.__inline, node.__key);
  }

  constructor(equation: string, inline?: boolean, key?: NodeKey) {
    super(key);
    this.__equation = equation;
    this.__inline = inline ?? false;
  }

  static importJSON(serializedNode: SerializedEquationNode): EquationNode {
    return $createEquationNode(serializedNode.equation, serializedNode.inline).updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedEquationNode {
    return {
      ...super.exportJSON(),
      equation: this.getEquation(),
      inline: this.__inline,
    };
  }

  createDOM(): HTMLElement {
    const element = document.createElement(this.__inline ? "span" : "div");
    // EquationNodes should implement `user-action:none` in their CSS to avoid
    // issues with deletion on Android.
    element.className = "e_eqn";
    return element;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement(this.__inline ? "span" : "div");
    // Encode the equation as base64 to avoid issues with special characters
    const equation = btoa(this.__equation);
    element.setAttribute("data-lexical-equation", equation);
    element.setAttribute("data-lexical-inline", `${this.__inline}`);
    // Don't render KaTeX HTML - just store the equation string
    // The EquationComponent will render it client-side when needed
    element.className = "e_eqn";
    // Add placeholder text content to prevent the element from being stripped
    // when serialized to HTML and re-parsed
    element.textContent = this.__equation;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        // Check if this is a lexical equation node (has the data attribute)
        if (domNode.hasAttribute("data-lexical-equation")) {
          return {
            conversion: $convertEquationElement,
            priority: 2,
          };
        }
        // Check if this might be a KaTeX-rendered equation node from backend
        // Look for inline attribute or katex class within the div
        if (
          domNode.hasAttribute("data-lexical-inline") ||
          Array.from(domNode.classList).includes("e_eqn") ||
          (domNode.firstChild instanceof HTMLElement && Array.from(domNode.firstChild.classList).includes("katex"))
        ) {
          return {
            conversion: $convertEquationElement,
            priority: 2,
          };
        }
        return null;
      },
      span: (domNode: HTMLElement) => {
        // Check if this is a lexical equation node (has the data attribute)
        if (domNode.hasAttribute("data-lexical-equation")) {
          return {
            conversion: $convertEquationElement,
            priority: 4,
          };
        }
        // Check if this might be a KaTeX-rendered equation node from backend
        if (
          domNode.hasAttribute("data-lexical-inline") ||
          Array.from(domNode.classList).includes("e_eqn") ||
          (domNode.firstChild instanceof HTMLElement && Array.from(domNode.firstChild.classList).includes("katex"))
        ) {
          return {
            conversion: $convertEquationElement,
            priority: 4,
          };
        }
        return null;
      },
    };
  }

  updateDOM(prevNode: this): boolean {
    // If the inline property changes, replace the element
    return this.__inline !== prevNode.__inline;
  }

  getTextContent(): string {
    return this.__equation;
  }

  getEquation(): string {
    return this.__equation;
  }

  setEquation(equation: string): void {
    const writable = this.getWritable();
    writable.__equation = equation;
  }

  decorate(): JSX.Element {
    return <EquationComponent equation={this.__equation} inline={this.__inline} nodeKey={this.__key} />;
  }
}

export function $createEquationNode(equation = "", inline = false): EquationNode {
  const equationNode = new EquationNode(equation, inline);
  return $applyNodeReplacement(equationNode);
}

export function $isEquationNode(node: LexicalNode | null | undefined): node is EquationNode {
  return node instanceof EquationNode;
}
